import { CalendarIcon, LinkIcon, MapPinIcon, TargetIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { loadOverview, type Overview } from '@/api/overview'
import { TradeFairState } from '@/api/tradeFairs'
import { useApiErrorMessage } from '@/api/errors'
import { useAuth } from '@/auth/useAuth'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { LeadsPerDayChart } from '@/components/dashboard/LeadsPerDayChart'
import { StatTile } from '@/components/dashboard/StatTile'
import { formatDate, formatPeriod } from '@/lib/dates'
import { fullName } from '@/lib/userDisplay'

/**
 * The screen a day starts on, answering the questions somebody opens it for: which fair is on, how
 * much has been written down there, and what is waiting.
 *
 * The figures come from one request and are already scoped to the caller — a salesperson's totals
 * are their own leads, an administrator's are everybody's.
 *
 * Each tile opens the lead list **on the rows it is a count of**, because the list keeps its
 * filters in the address bar: the copies still to be taken into the master data are
 * `/leads?unlinked=true`, this fair's leads are `/leads?fair=<id>`. A tile that led to an
 * unfiltered list would leave the reader to rebuild the cut its own number was made of.
 *
 * The chart loads separately from the figures — see [LeadsPerDayChart] — and only when there is a
 * fair to draw: its window is that fair's own days.
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const errorMessage = useApiErrorMessage()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadOverview(controller.signal)
      .then((loaded) => !controller.signal.aborted && setOverview(loaded))
      .catch((caught: unknown) => !controller.signal.aborted && setError(caught))
    return () => controller.abort()
  }, [])

  const name = user ? fullName(user) : ''
  const loading = overview === null && error === null

  return (
    <>
      <PageHeader
        // Two keys rather than an optional interpolation: a trailing comma dangling after a
        // missing name is the kind of thing only the source language gets away with.
        title={name ? t('dashboard.welcomeNamed', { name }) : t('dashboard.welcome')}
      />

      {/* Nothing but the error. A failed request left `overview` null, and rendering the cards
          against it would put three confident zeros on screen and announce that no fair is running
          — four statements about the business, every one of them made up. */}
      {error ? (
        <Alert variant="destructive">
          <AlertTitle>{t('dashboard.loadFailed')}</AlertTitle>
          <AlertDescription>{errorMessage(error, t('common.unexpectedError'))}</AlertDescription>
        </Alert>
      ) : (
        <Figures overview={overview} loading={loading} />
      )}
    </>
  )
}

function Figures({ overview, loading }: { overview: Overview | null; loading: boolean }) {
  const { t } = useTranslation()

  return (
    // One block that keeps its height. Loose in the layout's scrolling column, each card was a
    // flex item allowed to shrink — and a card clips its overflow, so on a phone the fair and the
    // chart came out cut in half rather than scrolling.
    <div className="flex shrink-0 flex-col gap-6">
      <TradeFairCard overview={overview} loading={loading} />

      <div className="grid gap-4 sm:grid-cols-2">
        <StatTile
          label={t('dashboard.notLinked')}
          value={overview?.notLinkedToCustomer ?? 0}
          icon={LinkIcon}
          to="/leads?unlinked=true"
          hint={t('dashboard.notLinkedHint')}
          loading={loading}
        />
        <StatTile
          label={t('dashboard.leadsTotal')}
          value={overview?.leadsTotal ?? 0}
          icon={TargetIcon}
          to="/leads"
          hint={t('dashboard.leadsTotalHint')}
          loading={loading}
        />
      </div>

      {/* Its own request, so it is not held up by the figures and does not hold them up either.
          No fair means no days to draw — and nothing worth reserving the space for. */}
      {loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        overview?.tradeFair && <LeadsPerDayChart tradeFairId={overview.tradeFair.id} />
      )}

      {/* So a copy left open on a tablet since yesterday does not read as this morning's figures. */}
      {overview && (
        <p className="text-muted-foreground text-xs">
          {t('dashboard.asOf', { date: formatDate(overview.today) })}
        </p>
      )}
    </div>
  )
}

/**
 * The fair, and the one number this screen leads with: how many leads have been written at it.
 *
 * Exactly one figure on the page is set at this size. It is the one somebody standing at a stand
 * actually wants, and making a second one compete with it would leave the page with no answer to
 * "what am I looking at".
 */
function TradeFairCard({ overview, loading }: { overview: Overview | null; loading: boolean }) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <Card className="grid gap-4 p-6">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-12 w-24" />
      </Card>
    )
  }

  const fair = overview?.tradeFair
  if (!fair) {
    return (
      <Card className="grid gap-2 p-6">
        <p className="font-medium">{t('dashboard.noTradeFair')}</p>
        <p className="text-muted-foreground text-sm">
          {t('dashboard.noTradeFairHint')}{' '}
          <Link to="/trade-fairs" className="underline underline-offset-4">
            {t('dashboard.toTradeFairs')}
          </Link>
        </p>
      </Card>
    )
  }

  const running = fair.state === TradeFairState.RUNNING

  return (
    <Card className="flex flex-row flex-wrap items-end justify-between gap-x-8 gap-y-6 p-6">
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-xl leading-tight font-semibold">{fair.name}</h2>
          <Badge variant={running ? 'default' : 'outline'}>
            {running ? t('tradeFairs.state.RUNNING') : t('tradeFairs.state.UPCOMING')}
          </Badge>
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="flex items-center gap-1.5">
            <CalendarIcon className="size-4" aria-hidden />
            {formatPeriod(fair.startDate, fair.endDate)}
          </span>
          <span className="flex items-center gap-1.5">
            <MapPinIcon className="size-4" aria-hidden />
            {fair.city}
            {/* Hall and stand are what somebody reads out on the phone to say where they are. */}
            {fair.hall && ` · ${t('tradeFairs.form.hall')} ${fair.hall}`}
            {fair.booth && ` · ${t('tradeFairs.form.booth')} ${fair.booth}`}
          </span>
        </div>
      </div>

      {/* The fair's own leads, which is what the number underneath counts. */}
      <Link
        to={`/leads?fair=${encodeURIComponent(fair.id)}`}
        className="grid gap-1 text-right max-sm:text-left"
      >
        <span className="text-5xl leading-none font-semibold">
          {overview?.leadsAtTradeFair ?? 0}
        </span>
        <span className="text-muted-foreground text-sm">{t('dashboard.leadsAtTradeFair')}</span>
      </Link>
    </Card>
  )
}
